import { hexColorRegEx } from '@logto/core-kit';
import { languageTagGuard } from '@logto/language-kit';
import { z } from 'zod';
import { Theme } from '../../types/theme.js';
export const colorGuard = z.object({
    primaryColor: z.string().regex(hexColorRegEx),
    isDarkModeEnabled: z.boolean(),
    darkPrimaryColor: z.string().regex(hexColorRegEx),
});
export const partialColorGuard = colorGuard.partial();
/** Maps a theme to the key of the logo URL in the {@link Branding} object. */
export const themeToLogoKey = Object.freeze({
    [Theme.Light]: 'logoUrl',
    [Theme.Dark]: 'darkLogoUrl',
});
export const brandingGuard = z
    .object({
    logoUrl: z.string().url(),
    darkLogoUrl: z.string().url(),
    favicon: z.string().url(),
    darkFavicon: z.string().url(),
})
    .partial();
export const languageInfoGuard = z.object({
    autoDetect: z.boolean(),
    fallbackLanguage: languageTagGuard,
});
export var SignInIdentifier;
(function (SignInIdentifier) {
    SignInIdentifier["Username"] = "username";
    SignInIdentifier["Email"] = "email";
    SignInIdentifier["Phone"] = "phone";
})(SignInIdentifier || (SignInIdentifier = {}));
export const signInIdentifierGuard = z.nativeEnum(SignInIdentifier);
export var AdditionalIdentifier;
(function (AdditionalIdentifier) {
    AdditionalIdentifier["UserId"] = "userId";
})(AdditionalIdentifier || (AdditionalIdentifier = {}));
export var AlternativeSignUpIdentifier;
(function (AlternativeSignUpIdentifier) {
    AlternativeSignUpIdentifier["EmailOrPhone"] = "emailOrPhone";
})(AlternativeSignUpIdentifier || (AlternativeSignUpIdentifier = {}));
export const signUpGuard = z.object({
    identifiers: z.nativeEnum(SignInIdentifier).array(),
    password: z.boolean(),
    verify: z.boolean(),
    secondaryIdentifiers: z
        .object({
        identifier: z.union([signInIdentifierGuard, z.nativeEnum(AlternativeSignUpIdentifier)]),
        verify: z.boolean().optional(),
    })
        .array()
        .optional(),
});
export const signInGuard = z.object({
    methods: z
        .object({
        identifier: z.nativeEnum(SignInIdentifier),
        password: z.boolean(),
        verificationCode: z.boolean(),
        isPasswordPrimary: z.boolean(),
    })
        .array(),
});
export const socialSignInGuard = z.object({
    automaticAccountLinking: z.boolean().optional(),
});
export const connectorTargetsGuard = z.string().array();
export const customContentGuard = z.record(z.string());
export var MfaFactor;
(function (MfaFactor) {
    MfaFactor["TOTP"] = "Totp";
    MfaFactor["WebAuthn"] = "WebAuthn";
    MfaFactor["BackupCode"] = "BackupCode";
    MfaFactor["EmailVerificationCode"] = "EmailVerificationCode";
    MfaFactor["PhoneVerificationCode"] = "PhoneVerificationCode";
})(MfaFactor || (MfaFactor = {}));
export const mfaFactorsGuard = z.nativeEnum(MfaFactor).array();
export var MfaPolicy;
(function (MfaPolicy) {
    /** @deprecated, use `PromptAtSignInAndSignUp` instead */
    MfaPolicy["UserControlled"] = "UserControlled";
    /** MFA is required for all users */
    MfaPolicy["Mandatory"] = "Mandatory";
    /** Ask users to set up MFA on their sign-in after registration (skippable, one-time prompt) */
    MfaPolicy["PromptOnlyAtSignIn"] = "PromptOnlyAtSignIn";
    /** Ask users to set up MFA during registration (skippable, one-time prompt) */
    MfaPolicy["PromptAtSignInAndSignUp"] = "PromptAtSignInAndSignUp";
    /** Do not ask users to set up MFA */
    MfaPolicy["NoPrompt"] = "NoPrompt";
})(MfaPolicy || (MfaPolicy = {}));
export var OrganizationRequiredMfaPolicy;
(function (OrganizationRequiredMfaPolicy) {
    /** Do not ask users to set up MFA */
    OrganizationRequiredMfaPolicy["NoPrompt"] = "NoPrompt";
    /** MFA is required for all users */
    OrganizationRequiredMfaPolicy["Mandatory"] = "Mandatory";
})(OrganizationRequiredMfaPolicy || (OrganizationRequiredMfaPolicy = {}));
export const mfaGuard = z.object({
    factors: mfaFactorsGuard,
    policy: z.nativeEnum(MfaPolicy),
    organizationRequiredMfaPolicy: z.nativeEnum(OrganizationRequiredMfaPolicy).optional(),
});
export const customUiAssetsGuard = z.object({
    id: z.string(),
    createdAt: z.number(),
});
export const captchaPolicyGuard = z.object({
    enabled: z.boolean().optional(),
});
export const sentinelPolicyGuard = z.object({
    maxAttempts: z.number().optional(),
    lockoutDuration: z.number().optional(),
});
export const emailBlocklistPolicyGuard = z.object({
    blockDisposableAddresses: z.boolean().optional(),
    blockSubaddressing: z.boolean().optional(),
    customBlocklist: z.string().array().optional(),
});
export var ForgotPasswordMethod;
(function (ForgotPasswordMethod) {
    ForgotPasswordMethod["EmailVerificationCode"] = "EmailVerificationCode";
    ForgotPasswordMethod["PhoneVerificationCode"] = "PhoneVerificationCode";
})(ForgotPasswordMethod || (ForgotPasswordMethod = {}));
export const forgotPasswordMethodsGuard = z.nativeEnum(ForgotPasswordMethod).array();
